/**
 * useMenuButton — accessible trigger + menu wiring for hand-rolled dropdown menus.
 *
 * TASK-028 P0-4 (PR-3). The header's custom dropdowns (user menu, care-recipient
 * selector) were openable but not closeable by keyboard, and exposed no ARIA.
 * This hook provides the WAI-ARIA menu-button behavior without pulling in a
 * component library (Radix is a deferred Non-Goal):
 *
 *   - Trigger gets aria-haspopup="menu" + aria-expanded reflecting real state.
 *   - Opening moves focus to the first menu item.
 *   - Escape closes AND returns focus to the trigger (the core bug: no keyboard
 *     way out). Tab closes without stealing focus. Arrow/Home/End roam items.
 *   - close() with returnFocus restores focus to the trigger — used by Escape,
 *     item selection, and mouse click-away — so the keyboard user is never
 *     stranded.
 *
 * Mouse behavior is preserved: click-to-toggle and click-away (via the caller's
 * backdrop) still work; focus simply also returns to the trigger on close.
 */
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';

function menuItems(menu: HTMLElement | null): HTMLElement[] {
  if (!menu) return [];
  return Array.from(menu.querySelectorAll<HTMLElement>('[role="menuitem"]'));
}

export function useMenuButton() {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const focusTrigger = useCallback(() => {
    // Focus the trigger before React unmounts the menu so the unmount cannot
    // bounce focus to <body>. The trigger lives outside the menu subtree.
    triggerRef.current?.focus();
  }, []);

  const close = useCallback(
    (returnFocus = true) => {
      setIsOpen(false);
      if (returnFocus) focusTrigger();
    },
    [focusTrigger],
  );

  const open = useCallback(() => setIsOpen(true), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  // Move focus into the menu when it opens.
  useEffect(() => {
    if (!isOpen) return;
    menuItems(menuRef.current)[0]?.focus();
  }, [isOpen]);

  const onMenuKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      const items = menuItems(menuRef.current);
      if (items.length === 0) return;
      const i = items.indexOf(document.activeElement as HTMLElement);
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          close(true);
          break;
        case 'ArrowDown':
          e.preventDefault();
          items[(i + 1) % items.length].focus();
          break;
        case 'ArrowUp':
          e.preventDefault();
          items[(i - 1 + items.length) % items.length].focus();
          break;
        case 'Home':
          e.preventDefault();
          items[0].focus();
          break;
        case 'End':
          e.preventDefault();
          items[items.length - 1].focus();
          break;
        case 'Tab':
          // Let Tab move focus naturally out of the menu, but close it.
          close(false);
          break;
      }
    },
    [close],
  );

  // ArrowDown/ArrowUp on the trigger opens the menu (Enter/Space already fire the
  // trigger's native onClick → toggle → focus-first via the effect above).
  const onTriggerKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      if (!isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        e.preventDefault();
        open();
      }
    },
    [isOpen, open],
  );

  return {
    isOpen,
    open,
    close,
    toggle,
    triggerRef,
    menuRef,
    triggerProps: {
      ref: triggerRef,
      'aria-haspopup': 'menu' as const,
      'aria-expanded': isOpen,
      'aria-controls': isOpen ? menuId : undefined,
      onKeyDown: onTriggerKeyDown,
    },
    menuProps: {
      ref: menuRef,
      id: menuId,
      role: 'menu' as const,
      onKeyDown: onMenuKeyDown,
    },
  };
}
